/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumLinkIcons, DownloadCardCtaFragment, DownloadCardFragment } from "@cosmos/hygraph-schema";
import { MouseEvent, ReactNode, useEffect, useState } from "react";

import {
	CosmosButton,
	CosmosButtonProps,
	CosmosDesc,
	CosmosRichText,
	CosmosTitle,
	EdsIcon,
	TextLinkAndChevron,
} from "@components/elements";
import { useDownloadLauncher, useIsMac, useIsMobile } from "@components/hooks";
import { cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

import { downloadCardStyles } from "./DownloadCard.style";

export const DownloadCardCta = ({
	cta,
	buttonSize = "lg",
}: {
	cta?: DownloadCardCtaFragment | null;
	buttonSize?: CosmosButtonProps["size"];
}): ReactNode => {
	const isMac = useIsMac();
	const isMobile = useIsMobile();
	const [windowsUrl, setWindowsUrl] = useState("");
	const [macUrl, setMacUrl] = useState("");
	useEffect(() => {
		if (cta?.__typename === "CosmosElementDownloadButton") {
			if (cta?.windowsUrl) setWindowsUrl(cta?.windowsUrl);
			if (cta?.macUrl) setMacUrl(cta?.macUrl);
		}
	}, [cta]);

	const downloadLauncher = useDownloadLauncher(windowsUrl, macUrl);

	switch (cta?.__typename) {
		case "CosmosElementButton":
			return <CosmosButton {...cta} size={buttonSize} />;

		case "CosmosElementDownloadButton": {
			const clickHandler = (e: MouseEvent): void => {
				e.preventDefault();
				downloadLauncher();
			};
			const downloadBtn = (
				<CosmosButton
					onClick={clickHandler}
					href={isMac ? macUrl : windowsUrl}
					label={cta.label}
					size={buttonSize}
					variant={cta.variant}
				/>
			);
			const mobileDisclaimer = (
				<CosmosRichText
					className="mobile-disclaimer"
					variant="uiMD"
					html={
						cta?.mobileDisclaimer?.html ||
						"Sorry, downloads are not available on mobile devices. Please try using Windows or Mac."
					}
				/>
			);
			return isMobile ? mobileDisclaimer : downloadBtn;
		}

		case "CosmosElementIconButton":
			return <CosmosButton {...cta} size={buttonSize} />;

		case "CosmosElementRichText":
			return <CosmosRichText className="cta-desc" variant="paragraphSM" html={cta.text.html} {...cta}></CosmosRichText>;

		case "CosmosElementLink":
			return <TextLinkAndChevron {...cta} icon={CosmosEnumLinkIcons.ChevronRight}></TextLinkAndChevron>;
	}
	return undefined;
};

export interface DownloadCardProps extends DownloadCardFragment {
	ctaElement?: React.ReactNode;
}

export function DownloadCard({ titleIcon, title, description, cta, ctaElement }: DownloadCardProps): React.JSX.Element {
	return (
		<VStack
			alignItems="flex-start"
			justifyContent="space-between"
			gap={{
				base: "eds.dimension.32",
				xs: "eds.dimension.24!",
			}}
			className={cx("download-card", downloadCardStyles)}
		>
			<VStack gap="eds.dimension.16" justify="start" alignItems="flex-start">
				<HStack gap="eds.dimension.16">
					{titleIcon && <EdsIcon icon={titleIcon} />}
					{title?.text && <CosmosTitle title={{ ...title, variant: "headingMD", as: "span" }} />}
				</HStack>
				{description?.html && <CosmosDesc description={{ ...description, variant: "paragraphSM" }} />}
			</VStack>
			{ctaElement ? ctaElement : cta ? <DownloadCardCta cta={cta} /> : null}
		</VStack>
	);
}
